import { useMemo, useState, type ReactNode } from "react";

import { cn } from "~/lib/utils";
import { MorrowAgentOrb } from "../brand/MorrowAgentOrb";
import { MorrowPersonAvatar } from "../workspace/MorrowPersonCard";
import { useTeamState } from "../workspace/morrowTeamStore";
import { IntegrationStepIcon } from "./IntegrationStepIcon";
import { MorrowStepContextIcon } from "./MorrowStepContextIcon";
import { MORROW_BROWSER_LOGO_URL } from "./morrowBuiltInToolDisplay";
import {
  describeToolCall,
  isMorrowNativeToolSlug,
  type WorkEntryIntegration,
} from "./workEntryStep";
import {
  isMorrowBrandedActor,
  matchWorkEntryActorMember,
  parseWorkEntryHeadingActor,
} from "./workEntryStepActor";

function useWorkEntryActorDirectory() {
  const team = useTeamState();
  return useMemo(
    () =>
      team.members.map((member) => ({
        name: member.name,
        ...(member.avatarSeed ? { avatarSeed: member.avatarSeed } : {}),
      })),
    [team.members],
  );
}

function shouldShowMorrowOrb(input: {
  readonly toolSlug?: string | null | undefined;
  readonly appName?: string | null | undefined;
  readonly integration?: WorkEntryIntegration | null | undefined;
  readonly actorName?: string | null | undefined;
}): boolean {
  if (input.toolSlug && isMorrowNativeToolSlug(input.toolSlug)) {
    return true;
  }
  if (input.integration && isMorrowNativeToolSlug(input.integration.slug)) {
    return true;
  }
  if (input.appName && isMorrowBrandedActor(input.appName)) {
    return true;
  }
  if (input.actorName && isMorrowBrandedActor(input.actorName)) {
    return true;
  }
  return false;
}

/** App / actor icon for compact work rows (timeline, agent activity, tickers). */
export function WorkEntryStepIcon({
  heading,
  integration,
  toolSlug,
  appName,
  logoUrl,
  className,
  fallback = null,
}: {
  readonly heading?: string | undefined;
  readonly integration?: WorkEntryIntegration | null | undefined;
  readonly toolSlug?: string | null | undefined;
  readonly appName?: string | null | undefined;
  readonly logoUrl?: string | null | undefined;
  readonly className?: string | undefined;
  readonly fallback?: ReactNode;
}) {
  const members = useWorkEntryActorDirectory();
  const [logoFailed, setLogoFailed] = useState(false);
  const actorName = heading ? parseWorkEntryHeadingActor(heading) : null;
  const teamMember =
    actorName && !isMorrowBrandedActor(actorName)
      ? matchWorkEntryActorMember(actorName, members)
      : null;

  const trimmedTool = toolSlug?.trim() ?? "";
  const described = useMemo(
    () => (trimmedTool ? describeToolCall(trimmedTool) : null),
    [trimmedTool],
  );
  const effectiveAppName = appName ?? described?.appName ?? null;
  const effectiveNativeSlug = described?.slug ?? (trimmedTool || null);
  const effectiveLogo =
    logoUrl ??
    described?.logoUrl ??
    (effectiveNativeSlug === "browser" ? MORROW_BROWSER_LOGO_URL : null);

  const iconClass = cn("shrink-0", className ?? "size-3.5");

  if (
    shouldShowMorrowOrb({
      toolSlug: effectiveNativeSlug,
      appName: effectiveAppName,
      integration,
      actorName,
    })
  ) {
    return <MorrowAgentOrb className={iconClass} />;
  }

  if (teamMember) {
    return (
      <MorrowPersonAvatar
        name={teamMember.name}
        {...(teamMember.avatarSeed ? { avatarSeed: teamMember.avatarSeed } : {})}
        {...(teamMember.imageUrl ? { imageUrl: teamMember.imageUrl } : {})}
        className={cn("shrink-0 rounded-[3px]", className ?? "size-3.5")}
      />
    );
  }

  if (integration) {
    if (integration.slug === "browser") {
      return (
        <img
          src={MORROW_BROWSER_LOGO_URL}
          alt=""
          aria-hidden
          className={cn("shrink-0 rounded-[3px] object-contain", className ?? "size-3.5")}
        />
      );
    }
    return <IntegrationStepIcon integration={integration} className={className} />;
  }

  if (effectiveLogo && !logoFailed) {
    return (
      <img
        src={effectiveLogo}
        alt=""
        aria-hidden
        className={cn("shrink-0 rounded-[3px] object-contain", className ?? "size-3.5")}
        onError={() => setLogoFailed(true)}
      />
    );
  }

  if (trimmedTool) {
    return <MorrowStepContextIcon toolName={trimmedTool} className={iconClass} />;
  }

  return fallback;
}
